/**
 * ═══ LES SMS REÇUS, CÔTÉ RÉGIE ═══
 *
 *   GET /domains/:d/events/:e/live/sms            la liste, et la réception ouverte ou non
 *   PUT /domains/:d/events/:e/live/sms {open}     ouvrir ou fermer la réception
 *
 * ⚠️ UN SEUL NUMÉRO POUR TOUS LES ÉVÉNEMENTS. Le routeur SIM n'envoie que l'expéditeur
 * et le texte, rien qui désigne un événement : c'est la régie qui OUVRE la réception,
 * et les SMS vont à l'événement qui écoute. Un seul à la fois — l'ouvrir ici la ferme
 * ailleurs (voir `api/src/routes/live/sms/update.js`).
 *
 * ⚠️ LES NUMÉROS ARRIVENT MASQUÉS, et c'est le serveur qui masque. Un numéro caché par
 * l'écran voyagerait quand même dans la réponse ; rien ici n'a à le refaire.
 *
 * Les règles pures sont ici, testées ; les appels vivent dans `control.ts`, la poussée
 * dans `channel.ts`.
 */

import { choiceNumber } from "./smsVote";
import type { Choice, Results } from "./types";

/** Un SMS, tel que la régie le reçoit. */
export type SmsMessage = {
  id: number;
  /** Masqué : `+33 6 •• •• •• 93`. Vide si le routeur ne l'a pas donné. */
  phone: string;
  text: string;
  /** ISO. L'heure d'arrivée chez nous : le routeur n'en envoie aucune. */
  receivedAt: string;
};

/** La réception de cet événement : ouverte ou non, et depuis quand. */
export type SmsState = { open: boolean; openedAt: string | null };

export type SmsInbox = SmsState & {
  /** Tous les SMS de l'événement, y compris ceux que la liste ne porte pas. */
  total: number;
  /** Les plus récents d'abord, au plus `SMS_LIST_LIMIT`. */
  messages: SmsMessage[];
};

/** Autant que le serveur en rend (`api/src/helpers/sms.js`). */
export const SMS_LIST_LIMIT = 500;

/**
 * UN SMS QUI VIENT D'ARRIVER, POSÉ SUR LA LISTE.
 *
 * ⚠️ RIEN TANT QUE LA LISTE N'EST PAS LUE. Un SMS poussé avant la première lecture
 * ferait une liste d'un seul SMS sous un total inventé ; la lecture, elle, l'aura.
 *
 * ⚠️ UN SMS DÉJÀ LÀ N'EST PAS COMPTÉ DEUX FOIS. La poussée et la relecture se croisent :
 * un SMS arrivé pendant que la liste se relisait est dans la réponse ET dans le canal.
 *
 * ⚠️ ET LA LISTE SE RANGE PAR NUMÉRO, pas par ordre d'arrivée des poussées : deux SMS à
 * la même seconde peuvent se doubler dans le réseau.
 */
export function withSms(
  inbox: SmsInbox | undefined,
  message: SmsMessage,
): SmsInbox | undefined {
  if (!inbox) return inbox;
  if (inbox.messages.some((m) => m.id === message.id)) return inbox;
  return {
    ...inbox,
    total: inbox.total + 1,
    messages: [message, ...inbox.messages]
      .sort((a, b) => b.id - a.id)
      .slice(0, SMS_LIST_LIMIT),
  };
}

/** La réception ouverte ou fermée — ici, ou ailleurs, ce qui l'a fermée ici. */
export function withSmsState(
  inbox: SmsInbox | undefined,
  state: SmsState,
): SmsInbox | undefined {
  return inbox ? { ...inbox, open: state.open, openedAt: state.openedAt } : inbox;
}

/**
 * CE QUE LE CANAL APPORTE, VÉRIFIÉ AVANT D'ÊTRE CRU. Un message mal formé ne doit pas
 * poser une ligne vide dans la liste.
 */
export function asSmsMessage(data: unknown): SmsMessage | null {
  const d = data as Partial<SmsMessage> | null | undefined;
  if (!d || typeof d.id !== "number" || typeof d.receivedAt !== "string") return null;
  return {
    id: d.id,
    phone: typeof d.phone === "string" ? d.phone : "",
    text: typeof d.text === "string" ? d.text : "",
    receivedAt: d.receivedAt,
  };
}

export function asSmsState(data: unknown): SmsState | null {
  const d = data as Partial<SmsState> | null | undefined;
  if (!d || typeof d.open !== "boolean") return null;
  return { open: d.open, openedAt: typeof d.openedAt === "string" ? d.openedAt : null };
}

/** L'heure d'arrivée, à la seconde : dans une salle, tout arrive le même jour. */
export function receivedTime(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * ═══ LA LISTE PAR PAGES (#350) ═══
 *
 *   GET /domains/:d/events/:e/live/sms?page=N&upTo=<id>   une page, la plus récente d'abord
 *
 * Un événement reçoit des milliers de SMS : la liste en portait cinq cents, d'un seul
 * tenant, et le reste ne se lisait nulle part. La première page est la liste en direct,
 * celle que le canal tient à jour ; les suivantes se lisent au serveur.
 *
 * ⚠️ LES PAGES SUIVANTES SONT TENUES PAR `upTo`, le SMS le plus récent affiché quand on
 * a quitté la première. Comptées depuis le plus récent tout court, elles glisseraient à
 * chaque SMS arrivé pendant qu'on les lit, et la suivante redonnerait la fin de celle-ci.
 * Revenir à la première page lâche la borne : on la reprend en repartant.
 */

/** Autant que le serveur en met par page (`api/src/helpers/sms.js`). */
export const SMS_PAGE_SIZE = 50;

/** Une page, telle que le serveur la rend. */
export type SmsPage = {
  page: number;
  pageSize: number;
  /** Les SMS jusqu'à `upTo` : ceux arrivés depuis sont sur la première page. */
  total: number;
  messages: SmsMessage[];
};

/** Où on en est, au-delà de la première page. */
export type SmsPaging = {
  page: number;
  /** Le SMS le plus récent affiché quand on a quitté la première page. */
  upTo: number;
  /** Combien il y en avait jusqu'à lui, en attendant que le serveur le dise. */
  total: number;
};

/** Combien de pages pour `total` SMS : une au moins, même vide. */
export function pageCount(total: number): number {
  return Math.max(1, Math.ceil(total / SMS_PAGE_SIZE));
}

/**
 * ALLER À LA PAGE `page` : `null` pour la première, la liste en direct.
 *
 * `known` est le compte que le serveur a rendu pour la borne, quand il l'a rendu : c'est
 * lui qui dit où est la dernière page.
 */
export function turnPage(
  current: SmsPaging | null,
  page: number,
  inbox: Pick<SmsInbox, "total" | "messages">,
  known?: number,
): SmsPaging | null {
  const newest = inbox.messages[0];
  if (page <= 1 || !newest) return null;
  const anchor = current
    ? { upTo: current.upTo, total: known ?? current.total }
    : { upTo: newest.id, total: inbox.total };
  return { ...anchor, page: Math.min(page, pageCount(anchor.total)) };
}

/**
 * ═══ REJOUER LES VOTES PAR SMS D'UNE ACTIVITÉ ═══
 *
 *   POST /domains/:d/events/:e/live/sms/replay {activityId, from, to}
 *
 * Les votes SMS de l'activité sont effacés, puis recomptés depuis les SMS reçus par
 * l'événement entre `from` et `to` — à l'écran ou non, ouverte ou non. C'est ce qui
 * rattrape des SMS envoyés trop tôt, trop tard, ou quand une autre activité était à
 * l'écran. Le serveur compte comme en direct (`api/src/helpers/sms.js`,
 * `replaySmsVotes`) : le premier SMS d'un numéro qui est un vote.
 *
 * ⚠️ SEULS LES VOTES SMS SONT EFFACÉS. Ceux de l'application ne se rejouent depuis
 * rien : le serveur n'y touche pas.
 */
export type SmsReplay = {
  activityId: number;
  /** Les SMS reçus dans la plage, votes ou non. */
  messages: number;
  /** Les numéros qui votent, une fois rejoués. */
  voters: number;
  /** Les numéros dont le vote SMS a été effacé avant le rejeu. */
  deletedVoters: number;
};

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * UNE DATE, TELLE QU'UN `<input type="datetime-local">` LA PORTE : `2026-09-24T14:05:30`,
 * à l'heure de l'appareil. Vide pour une date absente ou illisible.
 *
 * ⚠️ À LA SECONDE. Les SMS arrivent à la seconde : une fin arrondie à la minute
 * laisserait dehors ceux des dernières secondes.
 */
export function toLocalInput(value: string | Date | null | undefined): string {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

/**
 * LA PLAGE PROPOSÉE POUR UNE ACTIVITÉ : de sa dernière ouverture à sa clôture, ou à
 * maintenant si elle est encore ouverte. Ce n'est qu'une proposition — on la corrige
 * justement quand les SMS sont arrivés en dehors.
 *
 * ⚠️ UNE CLÔTURE ANTÉRIEURE À L'OUVERTURE EST CELLE D'AVANT. Rouverte, l'activité garde
 * la date de sa clôture précédente : la prendre ferait une plage à l'envers.
 */
export function replayWindowFor(
  activity: { phase: string; openedAt?: string | null; closedAt?: string | null },
  now: Date,
): { from: string; to: string } {
  const opened = activity.openedAt ? new Date(activity.openedAt) : null;
  const closed = activity.closedAt ? new Date(activity.closedAt) : null;
  const stillOpen =
    activity.phase === "open" || !closed || (!!opened && closed.getTime() < opened.getTime());
  return {
    from: toLocalInput(opened),
    to: toLocalInput(stillOpen ? now : closed),
  };
}

/**
 * LA PLAGE SAISIE, PRÊTE À PARTIR (en ISO, donc sans ambiguïté de fuseau) — ou ce qui
 * ne va pas, dit à l'organisateur. Le serveur refuse la même chose ; le dire avant
 * évite une confirmation pour rien.
 */
export function readReplayWindow(
  from: string,
  to: string,
): { from: string; to: string } | { error: string } {
  const start = from ? new Date(from) : null;
  const end = to ? new Date(to) : null;
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return { error: "Indiquez une date de début et une date de fin." };
  }
  if (start.getTime() >= end.getTime()) {
    return { error: "La fin doit venir après le début." };
  }
  return { from: start.toISOString(), to: end.toISOString() };
}

/**
 * LES SMS DE LA LISTE QUI TOMBENT DANS LA PLAGE, bornes comprises comme au serveur.
 *
 * ⚠️ CEUX DE LA LISTE : au-delà de `SMS_LIST_LIMIT`, la liste ne porte pas tout, et le
 * compte n'est qu'un minimum. L'écran le dit ; le serveur, lui, les lit tous.
 */
export function smsInWindow(
  messages: ReadonlyArray<Pick<SmsMessage, "receivedAt">>,
  window: { from: string; to: string },
): number {
  const from = new Date(window.from).getTime();
  const to = new Date(window.to).getTime();
  return messages.filter((m) => {
    const at = new Date(m.receivedAt).getTime();
    return at >= from && at <= to;
  }).length;
}

/**
 * ═══ LES RÉSULTATS AVANT ET APRÈS UN REJEU (#333) ═══
 *
 * Le rejeu disait combien de votes il avait effacés et rejoués, pas ce que ça changeait
 * aux résultats : il fallait avoir retenu les chiffres d'avant pour savoir si le rejeu
 * avait corrigé quelque chose. Réponse par réponse, les voix d'avant, celles d'après,
 * et l'écart.
 *
 * ⚠️ AVANT, C'EST LU AU MOMENT DE CONFIRMER, pas au choix de l'activité : le sondage à
 * l'écran continue de recevoir des votes pendant qu'on règle la plage.
 *
 * ⚠️ RIEN QUAND LES CHIFFRES MANQUENT. La régie les reçoit toujours, mais une API plus
 * ancienne n'envoie pas les résultats de la liste, et des résultats masqués n'ont que
 * des parts : un tableau de zéros affirmerait que le rejeu a tout effacé.
 */
export type ReplayCount = {
  votes: number;
  /** La part des réponses, de 0 à 1. */
  share: number;
};

export type ReplayComparison = {
  rows: {
    choiceId: number;
    /** Le numéro qu'on envoie par SMS pour cette réponse. */
    number: string;
    label: string;
    before: ReplayCount;
    after: ReplayCount;
  }[];
  votes: { before: number; after: number };
  /** `null` quand le serveur ne l'a pas dit. */
  voters: { before: number | null; after: number | null };
};

export function replayComparison(
  choices: ReadonlyArray<Pick<Choice, "id" | "label">>,
  before: Results | null | undefined,
  after: Results | null | undefined,
): ReplayComparison | null {
  if (!before || !after || before.hidden || after.hidden) return null;

  const total = (results: Results) =>
    results.totalVotes ??
    results.perChoice.reduce((sum, row) => sum + (row.votes ?? 0), 0);
  const count = (results: Results, choiceId: number): ReplayCount => {
    const votes = results.perChoice.find((row) => row.choiceId === choiceId)?.votes ?? 0;
    const all = total(results);
    return { votes, share: all ? votes / all : 0 };
  };

  return {
    rows: choices.map((choice, index) => ({
      choiceId: choice.id,
      number: choiceNumber(index),
      label: choice.label,
      before: count(before, choice.id),
      after: count(after, choice.id),
    })),
    votes: { before: total(before), after: total(after) },
    voters: { before: before.totalVoters ?? null, after: after.totalVoters ?? null },
  };
}

/** Un écart, signé : `+3`, `−2`, `0`. Le signe moins est le vrai, pas un tiret. */
export function signedGap(before: number, after: number): string {
  const gap = after - before;
  if (gap > 0) return `+${gap}`;
  if (gap < 0) return `−${-gap}`;
  return "0";
}
